import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import { api } from "../../lib/api";
import SiteHeader from "../../components/SiteHeader";
import SiteFooter from "../../components/SiteFooter";
import GuideHero from "./GuideHero";
import RichTextRenderer from "../../components/RichTextRenderer";

export default function GuidePage() {
  const { slug } = useParams();
  const [guide, setGuide] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setGuide(null);
    setNotFound(false);
    api.get(`/guides/${slug}`)
      .then((r) => setGuide(r.data))
      .catch((err) => {
        if (err.response?.status === 404) setNotFound(true);
      });
  }, [slug]);

  if (notFound) {
    return (
      <div className="bg-[#111111] min-h-screen">
        <SiteHeader />
        <div className="max-w-3xl mx-auto px-4 py-24 text-center">
          <p className="font-mono text-[#CC1A1A] text-sm tracking-widest uppercase mb-4">// 404</p>
          <p className="font-mono text-white text-xl mb-8">Guide not found.</p>
          <Link to="/guides" className="btn-rd inline-flex">← ALL GUIDES</Link>
        </div>
        <SiteFooter />
      </div>
    );
  }

  if (!guide) {
    return (
      <div className="bg-[#111111] min-h-screen">
        <SiteHeader />
        <div className="max-w-3xl mx-auto px-4 py-24 text-center">
          <p className="font-mono text-[#CC1A1A] text-xs tracking-widest uppercase animate-pulse">// LOADING…</p>
        </div>
        <SiteFooter />
      </div>
    );
  }

  const wordCount = (guide.content || "").replace(/<[^>]+>/g, " ").trim().split(/\s+/).filter(Boolean).length;
  const readTime = Math.max(1, Math.ceil(wordCount / 200));
  const eyebrow = guide.title.toUpperCase().split(":")[0];

  return (
    <div className="bg-[#111111] min-h-screen">
      <Helmet>
        <title>{guide.title} – Restricted Deals</title>
        {guide.meta_description && <meta name="description" content={guide.meta_description} />}
        <link rel="canonical" href={`https://restricteddeals.com/guides/${slug}`} />
      </Helmet>
      <SiteHeader />
      <GuideHero
        image={guide.hero_image}
        alt={guide.title}
        eyebrow={eyebrow}
        title={guide.title}
        photographerName={guide.photographer_name || null}
        objectPosition={guide.hero_position || null}
      />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <p className="text-[#555] font-mono text-xs tracking-widest uppercase mb-10">{readTime} min read</p>

        <RichTextRenderer content={guide.content} />

        <div className="hr-dot my-10" />
        <p className="text-[#9A8F83] font-sans text-sm leading-relaxed mb-8">
          Restricted Deals monitors fares daily and sends the best deals — mistake fares, flash sales, and genuine promotional offers — directly to your inbox.
        </p>
        <Link to="/subscribe" className="btn-rd inline-flex">GET FREE DEAL ALERTS →</Link>

        <div className="hr-dot my-10" />
        <Link to="/guides" className="font-mono text-[11px] tracking-widest uppercase text-[#555] hover:text-white transition-colors">← All Guides</Link>
      </main>
      <SiteFooter />
    </div>
  );
}
